/**
 * Alertas discretos pro chat: bip WebAudio curto + flash do título da aba
 * quando a aba está oculta. Sem dependência de arquivo de áudio — a nota
 * é gerada on-the-fly, então não há fetch/asset.
 *
 * - `playChatPing()` — tink curto (~300ms, 880→660Hz, vol 0.08)
 * - `isChatSoundEnabled()` / `setChatSoundEnabled()` — preferência local
 * - `startTitleFlash(count)` / `stopTitleFlash()` — trocar título quando
 *   a aba está oculta (`document.hidden`)
 */

const SOUND_KEY = 'dratento-chat-sound';

export function isChatSoundEnabled(): boolean {
  try {
    const raw = localStorage.getItem(SOUND_KEY);
    // Default ON
    if (raw === null) return true;
    return raw === '1';
  } catch {
    return true;
  }
}

export function setChatSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, enabled ? '1' : '0');
  } catch {
    // localStorage indisponível — silencioso
  }
}

// ── Volume do alerta (preferência local, por usuário) ──────────────────
// 0–100. Calibrado pra que 50 reproduza EXATAMENTE o volume histórico do
// ping (0.08 de ganho); 100 = ~2× mais alto; 0 = mudo. Aplicado como fator
// multiplicativo sobre o ganho de pico em `emitPing`/`playChatAttentionPing`.
const VOLUME_KEY = 'dratento-chat-volume';
/** Volume padrão (0–100). 50 = volume original do ping, pra não surpreender. */
export const DEFAULT_CHAT_VOLUME = 50;
/** Ganho de pico do ping a 100% de volume (a 50% → 0.08, o valor histórico). */
const PING_MAX_GAIN = 0.16;
/**
 * Ganho de pico do alerta de ATENÇÃO a 100% de volume. O timbre é de SINO
 * (parciais senoidais somadas e NORMALIZADAS pela soma das amplitudes), então
 * 0.7 soa forte e limpo sem estourar. A 50% do slider → ~0.35; a 100% → 0.7.
 */
const ATTENTION_MAX_GAIN = 0.7;

export function getChatVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw === null) return DEFAULT_CHAT_VOLUME;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_CHAT_VOLUME;
    return Math.min(100, Math.max(0, n));
  } catch {
    return DEFAULT_CHAT_VOLUME;
  }
}

export function setChatVolume(value: number): void {
  try {
    const clamped = Math.min(100, Math.max(0, Math.round(value)));
    localStorage.setItem(VOLUME_KEY, String(clamped));
  } catch {
    // localStorage indisponível — silencioso
  }
}

/** Fator 0..1 aplicado ao ganho dos beeps. */
function chatVolumeFactor(): number {
  return getChatVolume() / 100;
}

// Reusa um único AudioContext (browsers modernos limitam quantos podem existir
// e criar um novo a cada ping causaria warning e latência).
let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (sharedCtx) return sharedCtx;
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return null;
    sharedCtx = new AudioCtx();
    return sharedCtx;
  } catch {
    return null;
  }
}

// ── AudioContext unlock ────────────────────────────────────────────────
// Browsers só permitem `ctx.resume()` dentro de um user gesture. Sem isso,
// o primeiro `playChatPing()` falha silenciosamente e os usuários nunca
// ouvem o alerta até clicarem no balão. Resolve-se ouvindo o PRIMEIRO
// click/keydown/touchstart em qualquer lugar do documento — isso conta
// como gesture e desbloqueia o áudio pra alertas subsequentes.
if (typeof document !== 'undefined') {
  let unlocked = false;
  const unlock = () => {
    if (unlocked) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => { unlocked = true; }).catch(() => {});
    } else {
      unlocked = true;
    }
  };
  // capture:true para apanhar antes de stopPropagation em filhos
  document.addEventListener('click', unlock, { capture: true });
  document.addEventListener('keydown', unlock, { capture: true });
  document.addEventListener('touchstart', unlock, { capture: true, passive: true });
}

/**
 * Emite o "tink" discreto de ~300ms no volume configurado pelo usuário.
 * NÃO checa o mute (`isChatSoundEnabled`) — é o emissor cru, usado tanto
 * pelo alerta real (`playChatPing`) quanto pelo botão "Testar" das Configs
 * (`previewChatPing`). Falha silenciosamente se o áudio ainda não foi
 * autorizado pelo navegador (user gesture required) ou se o volume é 0.
 */
function emitPing(): void {
  const factor = chatVolumeFactor();
  if (factor <= 0) return; // volume 0 = mudo
  const ctx = getCtx();
  if (!ctx) return;
  // Alguns browsers suspendem o ctx até um gesto; tenta retomar
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.15);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(PING_MAX_GAIN * factor, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
  } catch {
    // Ignora — não vamos quebrar a UI se o áudio falhar
  }
}

/**
 * Toca o alerta de chat (tink curto). Respeita o mute do usuário e o volume
 * configurado nas Configurações › Som do Chat.
 */
export function playChatPing(): void {
  if (!isChatSoundEnabled()) return;
  emitPing();
}

/**
 * Toca o ping no volume atual IGNORANDO o mute — pro botão "Testar" da tela
 * de Configurações, pra que o usuário ouça o nível escolhido mesmo se o
 * alerta estiver desligado. Continua respeitando volume 0 (mudo).
 */
export function previewChatPing(): void {
  emitPing();
}

/**
 * Toca o alerta de "pedir atenção" como um "ding-dong" de campainha: dois
 * toques de SINO em queda (~1175 Hz → 880 Hz). Cada toque soma parciais
 * senoidais ligeiramente inarmônicas (razões 1, 2, 2.41, 3, 4.2, 5.4) com
 * ataque seco e cauda que decai — é o timbre metálico/cristalino de sino.
 *
 * Respeita o mute do usuário local — quem pede atenção NÃO pode forçar quem
 * silenciou a ouvir — e o volume configurado nas Configurações › Som do Chat.
 */
export function playChatAttentionPing(): void {
  if (!isChatSoundEnabled()) return;
  const factor = chatVolumeFactor();
  if (factor <= 0) return; // volume 0 = mudo
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  try {
    const now = ctx.currentTime;
    const peak = ATTENTION_MAX_GAIN * factor;
    // Parciais do sino: [razão de frequência, amplitude relativa, fator da
    // cauda]. As razões não-inteiras (2.41, 4.2, 5.4) dão o brilho inarmônico
    // característico de sino; as parciais agudas decaem mais rápido.
    const partials: [number, number, number][] = [
      [1.0, 1.0, 1.0],
      [2.0, 0.6, 0.85],
      [2.41, 0.35, 0.65],
      [3.0, 0.22, 0.55],
      [4.2, 0.14, 0.4],
      [5.4, 0.09, 0.3],
    ];
    const ampSum = partials.reduce((s, p) => s + p[1], 0);

    // Um toque de sino em `freq`, com pico ≈ `level` e cauda de ~`decay` s.
    const strike = (start: number, freq: number, level: number, decay: number) => {
      for (const [ratio, amp, decScale] of partials) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq * ratio, start);
        // Normaliza pela soma das amplitudes: o pico do conjunto fica ≈ level,
        // então nem no ataque (todas as parciais juntas) o sinal clipa.
        const a = (level / ampSum) * amp;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.linearRampToValueAtTime(a, start + 0.004); // ataque seco
        gain.gain.exponentialRampToValueAtTime(0.0001, start + decay * decScale);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + decay * decScale + 0.05);
      }
    };

    strike(now, 1174.66, peak, 0.9);      // "ding" — Ré6
    strike(now + 0.22, 880, peak, 1.2);   // "dong" — Lá5
  } catch {
    // Ignora
  }
}

// ─── Flash do título quando a aba está oculta ──────────────────────────

let originalTitle: string | null = null;
let flashInterval: number | null = null;

export function startTitleFlash(unreadCount: number): void {
  if (typeof document === 'undefined') return;
  if (!document.hidden) return; // só flasha quando a aba NÃO está visível

  if (originalTitle === null) originalTitle = document.title;
  if (flashInterval !== null) return; // já está flashando

  const alertTitle = `● ${unreadCount} nova${unreadCount === 1 ? '' : 's'} mensage${
    unreadCount === 1 ? 'm' : 'ns'
  } — DRAtento`;
  let flipped = false;
  flashInterval = window.setInterval(() => {
    document.title = flipped ? originalTitle! : alertTitle;
    flipped = !flipped;
  }, 1500);
}

export function stopTitleFlash(): void {
  if (flashInterval !== null) {
    clearInterval(flashInterval);
    flashInterval = null;
  }
  if (originalTitle !== null && typeof document !== 'undefined') {
    document.title = originalTitle;
  }
  originalTitle = null;
}

// Limpa o flash automaticamente quando a aba volta a ficar visível
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) stopTitleFlash();
  });
}
